import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, ShieldCheck, Lock, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { login } from '../utils/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success('Signed in. Welcome back.');
      const name = encodeURIComponent(`${data.user?.first_name || ''} ${data.user?.last_name || ''}`.trim());
      window.location.href = `https://app.esgine.io/?token=${data.token}&role=${data.user?.role || 'viewer'}&email=${encodeURIComponent(data.user?.email || '')}&name=${name}`;
    } catch (err) {
      setError(err.message || 'Incorrect email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FAFAF5] text-[#0B1F2D] flex flex-col">
      <header className="border-b border-[#E8E5DC]">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/esgine-logo.png" alt="ESGine" className="w-10 h-10 object-contain" />
            <div className="font-display text-[22px] leading-none tracking-tight text-[#0B1F2D]">
                ESGine<sup style={{fontSize:'9px', verticalAlign:'middle', marginLeft:'2px'}}>®</sup>
          </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-[#0B1F2D]/70 hover:text-[#0B1F2D] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to site
          </Link>
        </div>
      </header>

      <section className="flex-1 grid grid-cols-1 lg:grid-cols-12">
        <aside className="hidden lg:flex lg:col-span-6 relative bg-[#0B1F2D] text-[#FAFAF5] overflow-hidden">
          <div className="absolute inset-0 grain opacity-30" />
          <div className="absolute -top-32 -right-20 w-[480px] h-[480px] rounded-full bg-[#1B6F5E]/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-10 w-[420px] h-[420px] rounded-full bg-[#7BC2B0]/10 blur-3xl" />
          <div className="relative z-10 flex flex-col justify-between p-12 lg:p-16 w-full">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-[11.5px] tracking-[0.12em] uppercase text-[#FAFAF5]/80">
                <span className="relative w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-[#7BC2B0]" />
                  <span className="live-dot" />
                </span>
                Secure client portal
              </div>
              <h1 className="mt-10 font-display text-[48px] lg:text-[64px] leading-[0.98] tracking-tight">
                Sign in to your<br />
                <span className="italic text-[#7BC2B0]">compliance dashboard.</span>
              </h1>
              <p className="mt-6 text-[15.5px] leading-[1.6] text-[#FAFAF5]/70 max-w-[420px]">
                Powered by ESG-as-Code<sup>®</sup>. Access clause-traceable evaluations, framework rule sets, and audit-ready reports.
              </p>
            </div>
            <div className="mt-12 space-y-4">
              {[
                { Ico: ShieldCheck, t: 'TLS 1.3, encryption at rest, access-controlled.' },
                { Ico: Lock, t: 'Single tenant rule libraries and version history.' },
                { Ico: Sparkles, t: 'Clause-traceable outcomes across seven frameworks.' },
              ].map(({ Ico, t }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center text-[#7BC2B0]">
                    <Ico className="w-4 h-4" />
                  </div>
                  <div className="text-[13.5px] text-[#FAFAF5]/80">{t}</div>
                </div>
              ))}
            </div>
            <div className="mt-12 pt-6 border-t border-white/10 flex items-center justify-between text-[11.5px] text-[#FAFAF5]/55">
              <span>© {new Date().getFullYear()} ESGine®. All rights reserved.</span>
              <span className="font-mono-tight">v2026.1</span>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-6 flex items-center justify-center px-6 py-16 lg:p-16">
          <div className="w-full max-w-[440px]">
            <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">Client Login</div>
            <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">Welcome back.</h2>
            <p className="mt-3 text-[14.5px] leading-[1.6] text-[#0B1F2D]/65">
              Sign in with the credentials provided by the ESGine<sup>®</sup> team to access your compliance dashboard.
            </p>

            <form onSubmit={onSubmit} className="mt-9 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">Email address</Label>
                <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-12 bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E]" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">Password</Label>
                  <button type="button" onClick={() => toast.info('Contact info@esgine.io to reset your password.')} className="text-[12px] text-[#0B1F2D]/55 hover:text-[#0B1F2D] transition-colors">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input id="password" type={show ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="h-12 bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E] pr-11" />
                  <button type="button" onClick={() => setShow((v) => !v)} aria-label="Toggle password visibility" className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-[#0B1F2D]/55 hover:text-[#0B1F2D] hover:bg-[#FAFAF5] transition-colors">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-[#0B1F2D]/30 data-[state=checked]:bg-[#0B1F2D] data-[state=checked]:text-[#FAFAF5] data-[state=checked]:border-[#0B1F2D]" />
                <Label htmlFor="remember" className="text-[13px] text-[#0B1F2D]/75 cursor-pointer">Keep me signed in on this device</Label>
              </div>

              {error && (
                <div className="rounded-lg border border-[#A4243B]/25 bg-[#FBEAEA] text-[#A4243B] text-[12.5px] leading-[1.55] px-4 py-3">{error}</div>
              )}

              <Button type="submit" disabled={loading} className="w-full bg-[#0B1F2D] hover:bg-[#0B1F2D]/90 text-[#FAFAF5] rounded-full h-12 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                {loading ? 'Signing in...' : 'Sign In'}
                {!loading && <ArrowUpRight className="w-4 h-4" />}
              </Button>
            </form>

            <div className="mt-10 rounded-2xl border border-[#E8E5DC] bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[13.5px] font-medium text-[#0B1F2D]">Don't have access yet?</div>
                <div className="text-[12.5px] text-[#0B1F2D]/60 mt-0.5">New clients are reviewed within 1 to 2 business days.</div>
              </div>
              <a href="/#contact" className="inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-full border border-[#0B1F2D] text-[#0B1F2D] text-[13px] font-medium hover:bg-[#0B1F2D] hover:text-[#FAFAF5] transition-colors">Apply</a>
            </div>

            <div className="mt-10 text-[11.5px] text-[#0B1F2D]/55">
              For login assistance, contact{' '}
              <a href="mailto:info@esgine.io" className="text-[#0B1F2D] underline underline-offset-2">info@esgine.io</a>.
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
